import React, { useState, useEffect } from 'react';
import { Search, Save, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { adminAPI } from '../services/api';
import DisplayNameEditor from './DisplayNameEditor';

const SkinMappingInterface = ({ demoSkin, onMappingSaved, onSkip }) => {
  const [selectedSkin, setSelectedSkin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Auto-search when component mounts with demo skin name
  useEffect(() => {
    if (demoSkin?.demo_skin_name) {
      setSearchTerm(demoSkin.demo_skin_name);
      searchSkins(demoSkin.demo_skin_name);
    }
  }, [demoSkin]);

  const searchSkins = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await adminAPI.searchSkins(query, demoSkin?.demo_weapon_name || '', 1000);
      setSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      searchSkins(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handleSaveMapping = async () => {
    if (!selectedSkin || !demoSkin) return;

    setSaving(true);
    try {
      await adminAPI.saveSkinMapping(
        demoSkin.demo_skin_name,
        demoSkin.demo_weapon_name,
        selectedSkin.id,
        'admin'
      );
      
      onMappingSaved && onMappingSaved({
        demoSkin,
        mappedSkin: selectedSkin
      });
    } catch (error) {
      console.error('Failed to save mapping:', error);
      alert('Failed to save mapping: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getRarityBadgeClass = (rarity) => {
    const rarityClasses = {
      'Consumer Grade': 'bg-gray-400 text-white',
      'Industrial Grade': 'bg-blue-500 text-white',
      'Mil-Spec': 'bg-blue-600 text-white',
      'Restricted': 'bg-purple-500 text-white',
      'Classified': 'bg-pink-500 text-white',
      'Covert': 'bg-red-500 text-white',
      'Contraband': 'bg-yellow-500 text-black'
    };
    
    return rarityClasses[rarity] || 'bg-gray-400 text-white';
  };

  if (!demoSkin) return null;

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
          Map Demo Skin to Database
        </h3>
        
        <div className="bg-gray-700 rounded p-4 mb-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-400">Demo Weapon</div>
              <div className="text-white font-medium">{demoSkin.demo_weapon_name}</div>
            </div>
            <div>
              <div className="text-sm text-gray-400">Demo Skin Name</div>
              <div className="text-white font-medium">{demoSkin.demo_skin_name}</div>
            </div>
            <div>
              <div className="text-sm text-gray-400">Demo Paint Index</div>
              <div className="text-white font-medium">
                {demoSkin.demo_paint_index !== null ? (
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">{demoSkin.demo_paint_index}</span>
                ) : (
                  <span className="text-gray-500">Unknown</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Suggestions from backend */}
        {demoSkin.suggestions && demoSkin.suggestions.length > 0 && (
          <div className="mb-4">
            <div className="text-sm text-gray-400 mb-2">💡 Suggested Matches:</div>
            <div className="grid grid-cols-1 gap-2">
              {demoSkin.suggestions.slice(0, 3).map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => setSelectedSkin(suggestion)}
                  className={`p-3 rounded border text-left transition-all ${
                    selectedSkin?.id === suggestion.id
                      ? 'border-purple-500 bg-purple-900 bg-opacity-20'
                      : 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {suggestion.image && (
                      <img
                        src={suggestion.image}
                        alt={suggestion.name}
                        className="w-10 h-10 object-contain bg-gray-600 rounded"
                      />
                    )}
                    <div className="flex-1">
                      <div className="text-white font-medium text-sm">{suggestion.name}</div>
                      <div className="text-gray-400 text-xs space-y-1">
                        <div>{suggestion.match_type === 'exact_pattern' ? '🎯 Exact Pattern' : '🔍 Fuzzy Match'}</div>
                        {suggestion.pattern_id && (
                          <div className="flex gap-2 flex-wrap">
                            <span className="bg-purple-600 px-1.5 py-0.5 rounded text-xs text-white">
                              ID: {suggestion.pattern_id}
                            </span>
                            {suggestion.paint_index && (
                              <span className="bg-blue-600 px-1.5 py-0.5 rounded text-xs text-white">
                                Paint: {suggestion.paint_index}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {suggestion.rarity?.name && (
                      <div className="ml-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${getRarityBadgeClass(suggestion.rarity.name)}`}>
                          {suggestion.rarity.name}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Search Interface */}
      <div className="mb-6">
        <div className="text-sm text-gray-400 mb-2">Search Database:</div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search for skins in database..."
            className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* Search Results */}
      {searchLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto mb-6">
          {searchResults.length > 0 ? (
            <div className="grid grid-cols-1 gap-2">
              {searchResults.map((skin) => (
                <button
                  key={skin.id}
                  onClick={() => setSelectedSkin(skin)}
                  className={`p-4 rounded border text-left transition-all ${
                    selectedSkin?.id === skin.id
                      ? 'border-purple-500 bg-purple-900 bg-opacity-20'
                      : 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {skin.image && (
                      <img
                        src={skin.image}
                        alt={skin.name}
                        className="w-12 h-12 object-contain bg-gray-600 rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="flex-1">
                      <DisplayNameEditor 
                        skin={skin} 
                        onUpdate={(skinId, newDisplayName) => {
                          // Update the search results with new display name
                          setSearchResults(prev => prev.map(s => 
                            s.id === skinId ? { ...s, display_name: newDisplayName } : s
                          ));
                        }}
                      />
                      <div className="text-gray-400 text-sm mt-1">{skin.weapon_name}</div>
                      {skin.pattern_name && (
                        <div className="text-gray-500 text-xs">Pattern: {skin.pattern_name}</div>
                      )}
                      {(skin.pattern_id || skin.paint_index) && (
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {skin.pattern_id && (
                            <span className="bg-purple-600 px-1.5 py-0.5 rounded text-xs text-white">
                              ID: {skin.pattern_id}
                            </span>
                          )}
                          {skin.paint_index && (
                            <span className="bg-blue-600 px-1.5 py-0.5 rounded text-xs text-white">
                              Paint: {skin.paint_index}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {skin.rarity?.name && (
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${getRarityBadgeClass(skin.rarity.name)}`}>
                        {skin.rarity.name}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : searchTerm.length >= 2 ? (
            <div className="text-center py-8 text-gray-400">
              <Info className="h-8 w-8 mx-auto mb-2" />
              <p>No skins found matching "{searchTerm}"</p>
            </div>
          ) : null}
        </div>
      )}

      {/* Selected Skin Preview */}
      {selectedSkin && (
        <div className="bg-gray-700 rounded p-4 mb-6">
          <div className="text-sm text-gray-400 mb-2">Selected Mapping:</div>
          <div className="flex items-center gap-4">
            {selectedSkin.image && (
              <img
                src={selectedSkin.image}
                alt={selectedSkin.name}
                className="w-16 h-16 object-contain bg-gray-600 rounded"
              />
            )}
            <div className="flex-1">
              <div className="text-white font-semibold">{selectedSkin.name}</div>
              <div className="text-gray-300">{selectedSkin.weapon_name}</div>
              {selectedSkin.pattern_name && (
                <div className="text-gray-400 text-sm">Pattern: {selectedSkin.pattern_name}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSaveMapping}
          disabled={!selectedSkin || saving}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? 'Saving...' : 'Save Mapping'}
        </button>
        
        <button
          onClick={onSkip}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default SkinMappingInterface;
